import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface InkInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const InkInsights: React.FC<InkInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Find active bullet index
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Bullet text entrance transitions
  const textTranslateY = interpolate(elapsedFrames, [0, 10], [20, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance animations (first bullet trigger)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 12], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 16, mass: 0.6, stiffness: 120 },
  });

  // Dynamic progress line calculations
  const bulletDurationFrames = ((activeBullet.endMs - activeBullet.startMs) / 1000) * fps;
  const progressPercent = Math.min((elapsedFrames / bulletDurationFrames) * 100, 100);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(9, 12, 16, 0.82)", // dark translucent slate
          border: "1.5px solid rgba(1, 150, 175, 0.25)", // teal glass border
          borderRadius: "24px",
          padding: "40px 36px",
          boxShadow: "0 20px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(1, 150, 175, 0.1)",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Large watermark index number in pink */}
        <div
          style={{
            position: "absolute",
            top: "-15px",
            right: "20px",
            fontFamily: "'Bebas Neue', cursive",
            fontSize: "160px",
            color: "rgba(238, 158, 172, 0.04)", // pink low opacity
            lineHeight: 1,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {String(activeIndex + 1).padStart(2, "0")}
        </div>

        {/* Counter and status header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "13px",
              fontWeight: 700,
              color: "rgba(253, 255, 252, 0.4)", // muted porcelain
              letterSpacing: "3px",
            }}
          >
            POINT {activeIndex + 1} OF {bullets.length}
          </div>

          {/* Dots tracker in orange and teal */}
          <div style={{ display: "flex", gap: "6px" }}>
            {bullets.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: idx === activeIndex ? "#F45E2E" : "rgba(1, 150, 175, 0.25)",
                }}
              />
            ))}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#01c6e0"
                accentColor="#F45E2E"
                size={68}
                style="glow"
                opacity={textOpacity}
              />
            ) : null;
          })()}
        </div>

        {/* Dynamic progress bar timeline in teal */}
        <div
          style={{
            width: "100%",
            height: "3px",
            background: "rgba(1, 150, 175, 0.15)",
            borderRadius: "2px",
            position: "relative",
            overflow: "hidden",
            zIndex: 2,
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: "100%",
              background: "#0196AF", // teal
              borderRadius: "2px",
            }}
          />
        </div>

        {/* Bullet text (huge and viral) */}
        <div
          style={{
            opacity: textOpacity,
            transform: `translateY(${textTranslateY}px)`,
            minHeight: "240px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <p
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: "54px",
              fontWeight: 800,
              color: "#fdfffc", // porcelain/white for high readability
              lineHeight: 1.3,
              margin: 0,
              padding: 0,
              letterSpacing: "-0.5px",
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 800,
                color: "#F45E2E", // orange
                borderBottom: "3px solid #0196AF", // teal underline
                display: "inline",
                textShadow: "0 0 10px rgba(244, 94, 46, 0.25)",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
